import { useEffect, useRef, useState } from 'react';
import { multiplayerService } from '../../services/MultiplayerService';
import { closeInterface } from '../../services/InterfaceService';
import { useMenuNavigation } from '../../hooks/menu/useMenuNavigation';
import { useNetSessionStore } from '../../store/netSessionSlice';
import { DEV_LOBBY_CODE } from '../../net/reconnect';
import { KeyDisplay } from './KeyDisplay';

type View = 'menu' | 'join' | 'hosting' | 'joined';

function viewForRole(role: 'offline' | 'host' | 'client'): View {
  return role === 'host' ? 'hosting' : role === 'client' ? 'joined' : 'menu';
}

export const MultiplayerScreen = () => {
  const role = useNetSessionStore((s) => s.role);
  const code = useNetSessionStore((s) => s.code);
  const playerCount = useNetSessionStore((s) => s.playerCount);

  const [view, setView] = useState<View>(() => viewForRole(role));
  const [input, setInput] = useState(import.meta.env.DEV ? DEV_LOBBY_CODE : '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Follow the session role: 'join' is a local sub-view of the offline menu.
  useEffect(() => {
    if (role !== 'offline') {
      setView(viewForRole(role));
    } else {
      setView((v) => (v === 'join' ? 'join' : 'menu'));
    }
  }, [role]);

  useEffect(() => {
    if (view === 'join') {
      inputRef.current?.focus();
    }
  }, [view]);

  const onHost = async () => {
    if (busy) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await multiplayerService.host();
    } catch {
      setError('Échec de l’hébergement.');
    } finally {
      setBusy(false);
    }
  };

  const onJoin = async () => {
    if (busy) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await multiplayerService.join(input.trim().toUpperCase());
    } catch {
      setError('Code invalide ou hôte injoignable.');
    } finally {
      setBusy(false);
    }
  };

  const { containerRef, focusedId } = useMenuNavigation({
    onConfirm: (id) => {
      if (id === 'host') {
        void onHost();
      } else if (id === 'goto-join') {
        setView('join');
      } else if (id === 'join-submit') {
        void onJoin();
      } else if (id === 'back') {
        setView('menu');
      } else if (id === 'leave') {
        multiplayerService.leave();
      }
    },
    onCancel: () => closeInterface(),
  });

  const navStyle = (id: string): React.CSSProperties => ({
    ...btnStyle,
    outline: focusedId === id ? '2px solid #8cf' : 'none',
  });

  return (
    <div ref={containerRef} className="multiplayer-screen" style={panelStyle}>
      {view === 'menu' && (
        <>
          <h3 style={titleStyle}>Multijoueur</h3>
          <button
            data-nav-row={0}
            data-nav-id="host"
            style={navStyle('host')}
            disabled={busy}
            onClick={() => void onHost()}
          >
            {busy ? 'Création…' : 'Héberger'}
          </button>
          <button
            data-nav-row={1}
            data-nav-id="goto-join"
            style={navStyle('goto-join')}
            onClick={() => setView('join')}
          >
            Rejoindre
          </button>
        </>
      )}

      {view === 'join' && (
        <>
          <h3 style={titleStyle}>Rejoindre une partie</h3>
          <input
            ref={inputRef}
            value={input}
            maxLength={4}
            placeholder="CODE"
            style={inputStyle}
            onChange={(e) => setInput(e.target.value.toUpperCase())}
          />
          <button
            data-nav-row={0}
            data-nav-id="join-submit"
            style={navStyle('join-submit')}
            disabled={busy}
            onClick={() => void onJoin()}
          >
            {busy ? 'Connexion…' : 'Se connecter'}
          </button>
          <button
            data-nav-row={1}
            data-nav-id="back"
            style={navStyle('back')}
            onClick={() => setView('menu')}
          >
            Retour
          </button>
        </>
      )}

      {view === 'hosting' && (
        <>
          <h3 style={titleStyle}>Partie hébergée</h3>
          <div>
            Code&nbsp;: <strong>{code ?? '—'}</strong>
          </div>
          <div>Joueurs&nbsp;: {playerCount}</div>
          <button
            data-nav-row={0}
            data-nav-id="leave"
            style={navStyle('leave')}
            onClick={() => multiplayerService.leave()}
          >
            Fermer la session
          </button>
        </>
      )}

      {view === 'joined' && (
        <>
          <h3 style={titleStyle}>Connecté à la partie</h3>
          <div>
            Code&nbsp;: <strong>{code ?? '—'}</strong>
          </div>
          <div>Joueurs&nbsp;: {playerCount}</div>
          <button
            data-nav-row={0}
            data-nav-id="leave"
            style={navStyle('leave')}
            onClick={() => multiplayerService.leave()}
          >
            Quitter
          </button>
        </>
      )}

      {error && <div style={{ color: '#f88' }}>{error}</div>}

      <div style={hintStyle}>
        <KeyDisplay context="interface" action="CONFIRM" size="sm" /> valider ·{' '}
        <KeyDisplay context="interface" action="CANCEL" size="sm" /> fermer
      </div>
    </div>
  );
};

const panelStyle: React.CSSProperties = {
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 9000,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  padding: 24,
  minWidth: 300,
  background: 'rgba(20,20,28,0.96)',
  color: '#fff',
  borderRadius: 8,
  textAlign: 'center',
};

const titleStyle: React.CSSProperties = { margin: '0 0 6px' };

const btnStyle: React.CSSProperties = {
  padding: '8px 14px',
  background: 'rgba(255,255,255,0.08)',
  color: '#fff',
  border: '1px solid rgba(255,255,255,0.2)',
  borderRadius: 6,
  cursor: 'pointer',
  font: '14px sans-serif',
};

const inputStyle: React.CSSProperties = {
  padding: '8px 14px',
  fontSize: 20,
  letterSpacing: 4,
  textAlign: 'center',
  textTransform: 'uppercase',
  background: 'rgba(0,0,0,0.4)',
  color: '#fff',
  border: '1px solid rgba(255,255,255,0.25)',
  borderRadius: 6,
};

const hintStyle: React.CSSProperties = {
  marginTop: 8,
  opacity: 0.6,
  fontSize: 12,
};
